import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import AbsoluteDropdownContainer from 'ui-library/AbsoluteDropdownContainer';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE} from 'ui-library/Colors';
import IndeterminateCheckbox from 'ui-library/IndeterminateCheckbox';
import {ScrollableTableColumn} from 'ui-library/scrollable/ScrollableTableConstants';
import {BasicEvent} from 'util/Types';

import {openCustomizeColumnsModal} from './CustomizeColumnsModal';

export function getVisibleColumns(
  columns: Array<ScrollableTableColumn>,
  hiddenColumnKeys: Array<string>
): Array<ScrollableTableColumn> {
  return columns.filter((column, index) => {
    const isPrimary = index === 0;
    return isPrimary || !hiddenColumnKeys.includes(column.accessorKey);
  });
}

export interface ColumnFilterDropdownProps {
  columns: Array<ScrollableTableColumn>;
  hiddenColumnKeys: Array<string>;
  onUpdateHiddenColumns: (hiddenColumnKeys: Array<string>) => void;
  /** Whether to show the show/hide column checkboxes. Defaults to `true`; `false` is reorder-only. */
  canFilterColumns?: boolean;
  /**
   * When true (and `onUpdateColumnOrder`/`reorderableColumns` are provided), the columns button opens
   * a single "Customize columns" modal for both reordering and showing/hiding columns, instead of the
   * checkbox dropdown. The chosen order and visibility are persisted per user.
   */
  canReorderColumns?: boolean;
  /** The columns the user may reorder, in their current display order. See `getReorderableColumns`. */
  reorderableColumns?: Array<ScrollableTableColumn>;
  /**
   * The full persisted column order (across all result types), used to preserve the saved order of
   * columns that are not part of the current result set when the user saves.
   */
  columnOrderKeys?: Array<string>;
  onUpdateColumnOrder?: (columnOrderKeys: Array<string>) => void;
}

interface ColumnFilterDropdownState {
  isOpen: boolean;
}

interface ColumnOptionProps {
  column: ScrollableTableColumn;
  hiddenColumnKeys: Array<string>;
  onUpdateHiddenColumns: (hiddenColumnKeys: Array<string>) => void;
}

export class ColumnOption extends React.Component<ColumnOptionProps> {
  _handleToggleFilter = (): void => {
    const isColumnHidden = this.props.hiddenColumnKeys.includes(this.props.column.accessorKey);
    if (isColumnHidden) {
      this.props.onUpdateHiddenColumns(
        this.props.hiddenColumnKeys.filter((accessorKey) => this.props.column.accessorKey !== accessorKey)
      );
    } else {
      this.props.onUpdateHiddenColumns([...this.props.hiddenColumnKeys, this.props.column.accessorKey]);
    }
  };

  override render(): JSX.Element {
    const isColumnHidden = this.props.hiddenColumnKeys.includes(this.props.column.accessorKey);
    return (
      <label
        className="columnFilterDropdown-item"
        key={`filter-${this.props.column.header}`}
        {...getTestProps({component: 'ColumnFilterDropdown', element: 'menuitem'})}
      >
        <div className="columnFilterDropdown-checkbox">
          <input
            type="checkbox"
            checked={!isColumnHidden}
            onChange={this._handleToggleFilter}
            className={css(styles.checkbox)}
          />
        </div>
        <div>{this.props.column.header}</div>
      </label>
    );
  }
}

/**
 * A dropdown that shows current columns and allows the user to hide them.
 *
 * The component should take all the columns for the current listing of items, both hidden and visible.
 * The parent listing component should handle filtering the list of columns based on the hiddenColumnKeys.
 * The list of hiddenColumnKeys can be a superset of the current columns, as this will handle when a column is
 * not applicable to the current listing of items, but can still be applied when the column later re-appears.
 */
class ColumnFilterDropdown extends React.Component<ColumnFilterDropdownProps, ColumnFilterDropdownState> {
  override state: ColumnFilterDropdownState = {isOpen: false};

  _handleClose = (): void => {
    this.setState({isOpen: false});
  };

  _toggleOpen = (e: BasicEvent): void => {
    this.setState({isOpen: !this.state.isOpen});
  };

  /** Whether the columns button opens the unified "Customize columns" modal (vs. the checkbox dropdown). */
  _usesCustomizeModal(): boolean {
    return Boolean(
      this.props.canReorderColumns &&
      this.props.onUpdateColumnOrder != null &&
      (this.props.reorderableColumns?.length ?? 0) > 0
    );
  }

  _handleCustomizeColumns = async (): Promise<void> => {
    const {reorderableColumns, onUpdateColumnOrder, onUpdateHiddenColumns, hiddenColumnKeys} = this.props;
    // `canFilterColumns` defaults to true (see `defaultProps`); only treat an explicit `false` as
    // reorder-only.
    const canFilterColumns = this.props.canFilterColumns !== false;
    if (reorderableColumns == null || onUpdateColumnOrder == null) {
      return;
    }
    const result = await openCustomizeColumnsModal(reorderableColumns, hiddenColumnKeys, canFilterColumns);
    if (result !== undefined) {
      onUpdateColumnOrder(this._mergeColumnOrder(result.orderedKeys, reorderableColumns));
      if (canFilterColumns) {
        onUpdateHiddenColumns(result.hiddenColumnKeys);
      }
    }
  };

  /**
   * Merges the newly-chosen order for the current result set back into the previously-saved order.
   * Expanded-search columns vary by result type, so saving with only the current columns would drop
   * other types' saved order. We refill each slot the previous order gave to a currently-present
   * column with the next key from the modal's new order, leaving columns absent from the current
   * result set in their saved positions (rather than shunting them to the tail). Currently-present
   * columns that had no slot in the previous order are appended.
   */
  _mergeColumnOrder(
    orderedKeys: Array<string>,
    reorderableColumns: Array<ScrollableTableColumn>
  ): Array<string> {
    const currentKeys = new Set(reorderableColumns.map((column) => column.accessorKey));
    const newOrderQueue = [...orderedKeys];
    const merged = (this.props.columnOrderKeys ?? []).map((key) =>
      currentKeys.has(key) && newOrderQueue.length > 0 ? newOrderQueue.shift()! : key
    );
    return [...merged, ...newOrderQueue];
  }

  _handleToggleAllFilters = (): void => {
    const currentColumnKeys = this.props.columns.map((column) => column.accessorKey);
    const areAllColumnsVisible = this.props.columns.every(
      (column) => !this.props.hiddenColumnKeys.includes(column.accessorKey)
    );
    if (areAllColumnsVisible) {
      // Hide all the current columns (in addition to the already hidden columns not in the current listing).
      this.props.onUpdateHiddenColumns([...this.props.hiddenColumnKeys, ...currentColumnKeys]);
    } else {
      // Show all the current columns.
      this.props.onUpdateHiddenColumns(
        this.props.hiddenColumnKeys.filter((accessorKey) => !currentColumnKeys.includes(accessorKey))
      );
    }
  };

  _renderMenu(): JSX.Element {
    return (
      <div className="dropdown-menu columnFilterDropdown-menu" role="menu">
        <label
          className="columnFilterDropdown-item"
          {...getTestProps({component: 'ColumnFilterDropdown', element: 'menuitem'})}
        >
          <div className="columnFilterDropdown-checkbox">
            <IndeterminateCheckbox
              items={this.props.columns}
              selectedMap={(column) => !this.props.hiddenColumnKeys.includes(column.accessorKey)}
              onChange={this._handleToggleAllFilters}
            />
          </div>
          <div>Show all columns</div>
        </label>
        <div className="columnFilterDropdown-border" />
        {this.props.columns.map((column) => (
          <ColumnOption
            column={column}
            key={column.accessorKey}
            onUpdateHiddenColumns={this.props.onUpdateHiddenColumns}
            hiddenColumnKeys={this.props.hiddenColumnKeys}
          />
        ))}
      </div>
    );
  }

  override render(): JSX.Element {
    if (this._usesCustomizeModal()) {
      return (
        <Tooltip container="body" placement={TooltipPlacement.TOP} title="Customize columns...">
          <div className="columnFilterDropdown">
            <Button
              aria-label="Customize columns"
              buttonStyle={ButtonStyle.CIRCLE_ICON}
              dataTestAttrs={{component: 'ColumnFilterDropdown', element: 'customizeColumns'}}
              icon="columns"
              onClick={this._handleCustomizeColumns}
              size={ButtonSize.SMALL}
            />
          </div>
        </Tooltip>
      );
    }
    return (
      <Tooltip container="body" placement={TooltipPlacement.TOP} title="Filter columns...">
        <div className="columnFilterDropdown">
          <AbsoluteDropdownContainer
            menu={this._renderMenu()}
            onRequestClose={this._handleClose}
            open={this.state.isOpen}
          >
            <Button
              aria-label="Filter columns"
              buttonStyle={ButtonStyle.CIRCLE_ICON}
              className={classNames({'is-active': this.state.isOpen})}
              icon="columns"
              onClick={this._toggleOpen}
              size={ButtonSize.SMALL}
            />
          </AbsoluteDropdownContainer>
        </div>
      </Tooltip>
    );
  }
}

const styles = StyleSheet.create({
  checkbox: {
    accentColor: BLUE,
  },
});

export default ColumnFilterDropdown;
